/**
 * @file Provides the U.S. tax withholding notice and the submit and
 * U.S.-person-change handlers that show it before a form submits.
 */
import { useCallback, useState, useRef, type ReactNode } from "react";

import { INVESTMENT_PROFILE_FIELD_MAP } from "@components/Domain";

import shouldConfirmUsTaxWithholding from "../helpers/shouldConfirmUsTaxWithholding";
import type {
  Form,
  Investment,
  OnIsUsPersonChange,
  OnIsUsCitizenChange,
} from "../types";

import UsTaxWithholdingNoticeModal from "./UsTaxWithholdingNoticeModal";

type SubmitAction = () => unknown;

export type OnSubmitConfirm = (
  parameters: unknown,
  action: SubmitAction,
) => unknown;

type FormWithNotice = Form & {
  confirmUsTaxWithholding?: () => Promise<boolean>;
};

type Options = {
  form: Form;
  refreshSchema?: () => void;
  onSubmitConfirm?: OnSubmitConfirm;
};

type FieldName = [string, string];

type Result = {
  notice: ReactNode;
  onIsUsPersonChange: OnIsUsPersonChange;
  onIsUsCitizenChange: OnIsUsCitizenChange;
  onSubmitConfirm: OnSubmitConfirm;
};

const IS_US_PERSON_FIELD = "isUsPerson";

/**
 * Runs the submit action directly, with no confirmation notice.
 */
const defaultOnSubmitConfirm: OnSubmitConfirm = (_parameters, action) =>
  action();

/**
 * Tracks the U.S. tax withholding notice's open state and confirmation,
 * gates the form's submit on it, and resets it when the U.S.-person or
 * the U.S.-citizen answer changes.
 */
const useUsTaxWithholdingNotice = ({
  form,
  refreshSchema = undefined,
  onSubmitConfirm: onSubmitConfirmCustom = defaultOnSubmitConfirm,
}: Options): Result => {
  const [ isOpen, setIsOpen ] = useState(false);

  const didConfirmRef = useRef(false);
  const pendingActionRef = useRef<SubmitAction | null>(null);
  const pendingResolveRef = useRef<((didConfirm: boolean) => void) | null>(null);

  /**
   * Resolves the pending confirmation promise, if a submit is waiting on
   * one, and clears it.
   */
  const resolvePending = (didConfirm: boolean): void => {
    const resolve = pendingResolveRef.current;

    pendingResolveRef.current = null;

    if (!resolve) {
      return;
    }

    resolve(didConfirm);
  };

  /**
   * Returns the name of the current profile's U.S.-person field, or null
   * when the form has no profile type yet.
   */
  const getIsUsPersonFieldName = (): FieldName | null => {
    const investment = form.getFieldValue([]) as Investment;
    const { profileType } = investment;

    if (!profileType) {
      return null;
    }

    const profileField = INVESTMENT_PROFILE_FIELD_MAP[profileType];

    if (!profileField) {
      return null;
    }

    return [ profileField, IS_US_PERSON_FIELD ];
  };

  /**
   * Clears the U.S.-person answer, so the investor must select it again,
   * and rebuilds the schema around the empty answer.
   */
  const clearIsUsPersonField = (name: FieldName): void => {
    form.setFieldValue(name, undefined);

    if (!refreshSchema) {
      return;
    }

    refreshSchema();
  };

  /**
   * Closes the notice without confirming, cancels the pending submit,
   * clears the U.S.-person answer and sends the investor back to it.
   */
  const onReview = (): void => {
    didConfirmRef.current = false;
    setIsOpen(false);
    pendingActionRef.current = null;
    resolvePending(false);

    const name = getIsUsPersonFieldName();

    if (!name) {
      return;
    }

    clearIsUsPersonField(name);
    form.scrollToField(name);
  };

  /**
   * Closes the notice as confirmed and resumes the pending submit action,
   * if one is waiting.
   */
  const onUnderstand = (): void => {
    didConfirmRef.current = true;
    setIsOpen(false);
    resolvePending(true);

    const pendingAction = pendingActionRef.current;

    pendingActionRef.current = null;

    if (!pendingAction) {
      return;
    }

    pendingAction();
  };

  /**
   * Forgets the confirmation and clears the U.S.-person answer after the
   * citizenship answer changes, so a hidden No does not survive it and the
   * notice shows again once the investor answers the question anew.
   */
  const onIsUsCitizenChange = (): void => {
    didConfirmRef.current = false;
    pendingActionRef.current = null;
    setIsOpen(false);
    resolvePending(false);

    const name = getIsUsPersonFieldName();

    if (!name) {
      return;
    }

    form.setFieldValue(name, undefined);
  };

  const onIsUsPersonChange = useCallback((isUsPerson: boolean): void => {
    if (isUsPerson) {
      didConfirmRef.current = false;
      pendingActionRef.current = null;
      setIsOpen(false);
      resolvePending(false);
      return;
    }

    pendingActionRef.current = null;
    setIsOpen(true);
  }, []);

  /**
   * Resolves once the investor confirms the tax withholding notice, or
   * resolves immediately when the investment does not need one.
   */
  const confirmUsTaxWithholding = (): Promise<boolean> => {
    const investment = form.getFieldValue([]) as Investment;
    const shouldConfirm = shouldConfirmUsTaxWithholding(investment);
    const didConfirm = didConfirmRef.current;
    const canSubmit = !shouldConfirm || didConfirm;

    if (canSubmit) {
      return Promise.resolve(true);
    }

    setIsOpen(true);

    return new Promise((resolve) => {
      pendingResolveRef.current = resolve;
    });
  };

  const formWithNotice = form as FormWithNotice;

  formWithNotice.confirmUsTaxWithholding = confirmUsTaxWithholding;

  const onSubmitConfirm = useCallback<OnSubmitConfirm>((parameters, action) => {
    const investment = form.getFieldValue([]) as Investment;
    const shouldConfirm = shouldConfirmUsTaxWithholding(investment);
    const didConfirm = didConfirmRef.current;
    const canSubmit = !shouldConfirm || didConfirm;

    if (canSubmit) {
      return onSubmitConfirmCustom(parameters, action);
    }

    /**
     * Runs the deferred submit once the investor confirms the notice.
     */
    pendingActionRef.current = () => onSubmitConfirmCustom(parameters, action);
    setIsOpen(true);
  }, [
    form,
    onSubmitConfirmCustom,
  ]);

  const notice = (
    <UsTaxWithholdingNoticeModal
      isOpen={isOpen}
      onReview={onReview}
      onUnderstand={onUnderstand}
    />
  );

  return {
    notice,
    onIsUsPersonChange,
    onIsUsCitizenChange,
    onSubmitConfirm,
  };
};

export default useUsTaxWithholdingNotice;
